import { Component, AfterViewInit, OnDestroy, inject, signal, WritableSignal, computed, Inject, PLATFORM_ID, ChangeDetectorRef, viewChild, NgZone } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatStep, MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { AddressService, NavegoAddress } from '../../shared/address.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { PersonalInformation, DirectSignupParams, SignupVerificationRecord, SignupVerificationState } from './signup.types';
import { CommonModule, isPlatformBrowser, Location } from '@angular/common';
import { AccountService } from '../../shared/account.service';
import { LocalStorageService } from '../../shared/local-storage.service';
import { SignalsStoreService } from '../../shared/signals-store.service';
import { Session } from '../../shared/types/session.type';
import { LOCALSTORAGE_KEYS } from '../../shared/constants/databases';
import { ArrayOptionsItem, NewAccountPayload, AccountSettings, DeliveryOptionsResponseItem, PremiumMembership, Reference } from '../../shared/types/account.types';
import { PaymentMethodService } from '../../settings/billing/payment-method/payment-method.service';
import { MatRadioModule } from '@angular/material/radio';
import { PersonalInformationSignupComponent } from './personal-information-signup/personal-information-signup.component';
import { CheckAddressSignupComponent } from './check-address-signup/check-address-signup.component';
import { PaymentMethodSignupComponent } from './payment-method-signup/payment-method-signup.component';
import { SubscriptionsSignupComponent } from './subscriptions-signup/subscriptions-signup.component';
import { ProfileService } from '../../settings/account/profile/profile.service';
import { finalize, tap } from 'rxjs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { ProductsService } from '../../product/products.service';
import { CsaOptionComponent } from './csa-option/csa-option.component';
import { Product, ProductAttributeValues } from '../../product/product.types';
import { SubscriptionsService } from '../../settings/account/subscriptions/subscriptions.service';
import { SubscriptionPayload } from '../../settings/account/subscriptions/subscriptions.types';
import { OrderService } from '../../shared/order.service';
import { DeliveriesService } from '../../settings/account/deliveries/deliveries.service';
import { HeaderService } from '../../shared/header/header.service';
import { NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ModalContentService } from '../../shared/modal-content/modal-content.service';
import { ApiResponse } from '../../shared/common/types';
import { HTTP_STATUS_CODES } from '../../shared/constants/errors';
import { ModalContentTypes } from '../../shared/constants/modal-content-types';
import { MarketingReferral } from '../../shared/types/common.types';
import { checkSignupCouponCodes } from '../../shared/common/singup.utils';
import { PromotionsService } from '../../shared/promotions.service';
import { NotificationService } from '../../shared/notification/notification.service';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { DataLayerService } from '../../shared/data-layer/data-layer.service';
import { DATA_LAYER_SIGN_UP_FUNNEL_STEP_OPTIONS, DataLayer_SignUpFunnel_Pickup } from '../../shared/data-layer/data-layer.types';

const RESEND_VERIFICATION_COOLDOWN_SECONDS = 60;
const SIGNUP_VERIFICATION_CHANNEL = 'signup-email-verification';
const VERIFICATION_TOKEN_QUERY_PARAM = 'verificationToken';

@Component({
  selector: 'app-signup',
  providers: [provideNativeDateAdapter(),
    PaymentMethodService,
    ProfileService
  ],
  imports: [
    CommonModule,
    MatButtonModule,
    MatStepperModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatAutocompleteModule,
    MatProgressBarModule,
    RouterLink,
    MatRadioModule,
    CheckAddressSignupComponent,
    PersonalInformationSignupComponent,
    PaymentMethodSignupComponent,
    CsaOptionComponent,
    SubscriptionsSignupComponent,
    NgbAccordionModule,
    NgbTooltip
  ],
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.scss'
})

export class SignupComponent implements AfterViewInit, OnDestroy {
  public localStorageService = inject(LocalStorageService);
  private signalsStoreService = inject(SignalsStoreService);
  private addressService = inject(AddressService);
  private productsService = inject(ProductsService);
  accountService = inject(AccountService);
  cdr = inject(ChangeDetectorRef)
  #headerService = inject(HeaderService);
  #deliveriesService = inject(DeliveriesService);
  #sanitizer = inject(DomSanitizer);
  #modalContentService = inject(ModalContentService);
  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #location = inject(Location);
  #orderService = inject(OrderService);
  #subscriptionsService = inject(SubscriptionsService);
  profileService = inject(ProfileService);
  #dataLayerService = inject(DataLayerService);
  #ngZone = inject(NgZone);

  matStepper = viewChild.required<MatStepper>('stepper');
  firstStep = viewChild.required<MatStep>('firstStep');
  personalInformationStep = viewChild.required<MatStep>('personalInformationStep');
  personalInformationComponent = viewChild.required<PersonalInformationSignupComponent>('personalInformationComponent');
  checkAddressComponent = viewChild.required<CheckAddressSignupComponent>('checkAddressComponent');
  paymentMethodStep = viewChild.required<MatStep>('paymentMethodStep');

  #promotionsService = inject(PromotionsService);
  #notificationService = inject(NotificationService);

  formValidityPersonalInformation: boolean | undefined;
  formValidityShoppingPreferences: boolean | undefined;
  formValidityPaymentMethod: boolean | undefined;
  deliveryAddressInformation: any;
  deliverySelectedDay: any;
  deliverySelectedTimeWindow: any;
  deliveryOptionSelected: any;
  personalInformationData: any;
  shopforValue: any;
  dietaryRestrictionsValues: any;
  paymentMethodStepControl: FormControl = new FormControl(true);

  personalInformation: WritableSignal<PersonalInformation | null> = signal(null)
  skipPaymentMethodStep: boolean = false;

  isLoadingContent: any = this.addressService.isLoading
  isWaitingResponse: any = signal(false)

  shouldDisableCreateClientButton = signal(false);

  shouldSetUpSettings: boolean = true;

  subscriptionCard: WritableSignal<any[]> = signal([])

  logoVersionNumber = computed(() => this.signalsStoreService.logoVersionNumber());

  hasGlobalMessages = computed(() => !!this.signalsStoreService.globalMessages().length);

  #premiumMembershipId!: number | undefined;
  premiumMemberships = signal<PremiumMembership[]>([]);
  signupPromotionInfo = computed(() => this.signalsStoreService.signupPromotionInfo());

  referralSource = signal<Reference[]>([]);

  isDirectSignup = signal(false);
  directSignupMembershipId = signal<number | null>(null);
  directSignupProduct = signal<Product | null>(null);
  directSignupVariant = signal<ProductAttributeValues | { variantId: number, name?: string, price?: number } | null>(null);
  directSignupEmail = signal('');
  pickupInfo = signal<DataLayer_SignUpFunnel_Pickup>({ location: '', day: '', zone: '' });
  #directSignupParams = signal<DirectSignupParams | null>(null);

  //#endregion

  //#region Email verification

  // Tab A: blocking "check your email" screen shown after Step 1.
  verificationPending = signal(false);

  // Tab A: the email was verified in the tab opened from the link.
  verifiedInOtherTab = signal(false);

  // Tab B: consuming the token from the email link.
  isVerifyingToken = signal(false);

  verificationError: WritableSignal<'expired' | 'invalid' | null> = signal(null);

  showVerifiedBanner = signal(false);

  isSendingVerification = signal(false);

  resendCooldown = signal(0);

  // Token consumed from the email link; required by the account-creation endpoint.
  verificationToken: string | null = null;

  #verifiedEmail: string | null = null;

  // Copy of the navegoAddress restored from the verification record. The address
  // autocomplete wipes the AddressService signal right after it initializes
  // (autocomplete-input ngAfterViewInit), so we re-assert it before creating the
  // account when the user arrived through the email link.
  #restoredNavegoAddress: NavegoAddress | null = null;

  #verificationChannel: BroadcastChannel | null = null;

  #storageListener: ((e: StorageEvent) => void) | null = null;

  #cooldownIntervalId: any = null;

  //#endregion

  faqs = [
    {
      question: 'Is this a standard CSA?',
      answer: 'No! We offer a unique advantage, we bring you farm fresh produce, eggs, meats, cheese and more but with the convenience of grocery store shopping. You can build your order exactly how you like it each week. '
    },
    {
      question: 'Am I locked into a contract?',
      answer: "Here's our iron-clad guarantee: If you're not completely satisfied with your Market Box after four orders, we'll refund your membership fee, no questions asked. Because that's what neighbors do."
    },
    {
      question: 'What if I need to skip a week?',
      answer: "Life happens. Maybe you're on vacation. Maybe your kids decided they only eat orange foods now. No problem. Pause or modify your order anytime."
    },
    {
      question: 'How much does it cost?',
      answer: "Unlike traditional CSAs that have fixed week-by-week prices or require an upfront annual payment, our memberships allow you to choose your level and customize your order each week. You'll be charged based on the total of your shopping cart each time you place an order, giving you the flexibility to control how much you spend."
    },
    {
      question: 'Will you charge my credit card?',
      answer: "After sign up we will only charge your credit card for your membership if you sign up for our Essentials or Sustainer membership. After that you will only be charged on the weeks when you have an active delivery. "
    },
    {
      question: 'I have a promo code. When will it apply to my order?',
      answer: `
      <p>Promo codes can apply in two ways:</p>
      <ol>
        <li class="mb-2">
          <strong>Automatic application</strong> – Some promo codes will apply automatically at checkout once you reach the required minimum order amount. You’ll see the discount reflected in your cart.
        </li>
        <li class="mb-2">
          <strong>Manual entry</strong> – Other promo codes need to be entered manually in the coupon field during checkout. Once applied, you’ll see the discount before completing your order.
        </li>
      </ol>
      `
    }
  ];

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    this.#detectDirectSignup();
  }

  isLoadedMarketingValidations = computed(() => this.signalsStoreService.isLoadedMarketingValidation());

  isTryingToApplyCoupon = signal(false);

  signupPromotionInfoFromLS = computed(() => this.signalsStoreService.signupPromotionInfoLS());

  // Detects the direct signup flow from the URL. Runs on the server as well as the
  // browser on purpose: the stepper hides the CSA and Subscriptions steps behind
  // @if (!isDirectSignup()), so if the server renders the 5-step version and the
  // client then flips the flag after hydration, the stepper is reconciled against a
  // different structure and ends up rendering no steps at all.
  #detectDirectSignup(): void {
    const params = this.#route.snapshot.queryParams;
    if (params['from'] !== 'product' || !params['pid'] || !params['vid'] || !params['mid']) return;

    const productId = Number(params['pid']);
    const variantId = Number(params['vid']);
    const membershipId = Number(params['mid']);
    if (isNaN(productId) || isNaN(variantId) || isNaN(membershipId)) return;

    this.isDirectSignup.set(true);
    this.#premiumMembershipId = membershipId;
    this.directSignupMembershipId.set(membershipId);
    this.#directSignupParams.set({ from: params['from'], productId, variantId, membershipId });
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // The flow itself was already detected before the view rendered; only the network
    // calls are deferred to the browser.
    const directParams = this.#directSignupParams();
    if (directParams) {
      this.#fetchDirectSignupProduct(directParams.productId, directParams.variantId);
      this.#headerService.getFrequencies();
    }

    const signupSelectedMembership: any = this.localStorageService.get(LOCALSTORAGE_KEYS.SIGNUP_SELECTED_MEMBERSHIP);
    if (signupSelectedMembership?.id)
      this.deliveryOptionSelected = signupSelectedMembership;

    const sessionStored: Session = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION) as any
    const hasPaymentMethod = !!(sessionStored)?.settings?.hasPaymentMethod;
    const paymentStepIndex = this.isDirectSignup() ? 2 : 3;
    const stepToLoad = this.signalsStoreService.hasSession() && !hasPaymentMethod ? paymentStepIndex : 0;

    // Load first step or payment method step if hasSession
    if (stepToLoad > 0) {
      if (!this.isDirectSignup()) {
        this.#getInfoForStep5();
      }
      for (let index = 0; index < stepToLoad; index++) {
        this.matStepper().steps.toArray()[index].completed = true;
      }
    }

    this.matStepper().selectedIndex = stepToLoad;
    this.cdr.detectChanges()

    // Tab opened from the verification email link: consume the token and restore
    // the Step 1 state saved in the temporal signup record.
    const verificationToken = this.#route.snapshot.queryParamMap.get(VERIFICATION_TOKEN_QUERY_PARAM);
    if (verificationToken && !this.signalsStoreService.hasSession())
      this.#handleVerificationToken(verificationToken);
  }

  ngOnDestroy(): void {
    clearInterval(this.#cooldownIntervalId);
    this.#verificationChannel?.close();
    if (this.#storageListener)
      window.removeEventListener('storage', this.#storageListener);
  }


  //#region Email verification

  #buildVerificationState(): SignupVerificationState {
    return {
      deliveryAddressInformation: this.deliveryAddressInformation?.() ?? null,
      deliverySelectedDay: this.deliverySelectedDay ?? null,
      deliverySelectedTimeWindow: this.deliverySelectedTimeWindow ?? null,
      deliveryOptionSelected: this.deliveryOptionSelected ?? null,
      navegoAddress: this.addressService.navegoAddress() ?? null,
      signupPromotionInfo: this.signupPromotionInfo() ?? null,
      pickupInfo: this.pickupInfo() ?? null,
      directSignupParams: this.#directSignupParams() ?? null,
      // The referral/campaign attribution lives only in THIS browser's storage, and the
      // emailed link carries no utm_* of its own. Without parking it here, a customer who
      // opens that link in any other browser creates the account with no attribution at
      // all, and the member who referred them is never credited (DEV-11494). Parking it
      // is what makes the referral survive the trip through the inbox.
      marketingReferral: (this.localStorageService.get(LOCALSTORAGE_KEYS.MARKETING_REFERRAL) as MarketingReferral | null) ?? null,
    };
  }

  #sendVerificationEmail(isFirstSend: boolean = false) {
    if (this.isSendingVerification() || (!isFirstSend && this.resendCooldown() > 0)) return;

    const email = this.directSignupEmail();
    if (!email) return;

    this.isSendingVerification.set(true);
    this.isWaitingResponse.set(true);

    this.accountService
      .createSignupVerification(email, this.#buildVerificationState())
      .pipe(finalize(() => {
        this.isSendingVerification.set(false);
        this.isWaitingResponse.set(false);
      }))
      .subscribe({
        next: (res: any) => {
          if (res?.error && res.status !== HTTP_STATUS_CODES.TOO_MANY_REQUESTS) {
            this.#notificationService.show({
              text: res.message || 'We could not send the verification email. Please try again.',
              type: 'error'
            });
            return;
          }

          if (res?.error)
            this.#notificationService.show({
              text: 'A verification email was sent recently. Please check your inbox.',
              type: 'warning'
            });
          else if (!isFirstSend)
            this.#notificationService.show({
              text: `Verification email sent to ${email}.`,
              type: 'success'
            });

          this.verificationError.set(null);
          this.verificationPending.set(true);
          this.#startResendCooldown();
          this.#initVerificationSync();
        }
      });
  }

  resendVerificationEmail() {
    this.#sendVerificationEmail();
  }

  changeVerificationEmail() {
    // Back to Step 1 (its state is preserved: the stepper is hidden, not destroyed)
    // so the user can fix a typo in the email and continue again.
    this.verificationPending.set(false);
    this.firstStep().completed = false;
  }

  restartSignupVerification() {
    this.verificationError.set(null);
  }

  #startResendCooldown() {
    if (!isPlatformBrowser(this.platformId)) return;
    clearInterval(this.#cooldownIntervalId);
    this.resendCooldown.set(RESEND_VERIFICATION_COOLDOWN_SECONDS);
    this.#cooldownIntervalId = setInterval(() => {
      const remaining = this.resendCooldown() - 1;
      this.resendCooldown.set(Math.max(0, remaining));
      if (remaining <= 0) clearInterval(this.#cooldownIntervalId);
    }, 1000);
  }

  #initVerificationSync() {
    if (!isPlatformBrowser(this.platformId) || this.#storageListener) return;

    if ('BroadcastChannel' in window) {
      this.#verificationChannel = new BroadcastChannel(SIGNUP_VERIFICATION_CHANNEL);
      this.#verificationChannel.onmessage = (e: MessageEvent) =>
        this.#ngZone.run(() => this.#onVerifiedSomewhereElse(e.data));
    }

    // Fallback for browsers without BroadcastChannel, plus session detection to
    // leave this tab once the account is fully created in the other one.
    this.#storageListener = (e: StorageEvent) => this.#ngZone.run(() => {
      if (e.key === LOCALSTORAGE_KEYS.SIGNUP_EMAIL_VERIFIED && e.newValue)
        this.#onVerifiedSomewhereElse({ type: 'verified' });
      // Account fully created in the other tab: leave with a full navigation so
      // this tab picks up the new session from localStorage.
      if (e.key === LOCALSTORAGE_KEYS.SESSION && e.newValue && (this.verifiedInOtherTab() || this.verificationPending()))
        window.location.assign('/shop');
    });
    window.addEventListener('storage', this.#storageListener);
  }

  #onVerifiedSomewhereElse(data: any) {
    if (data?.type !== 'verified' || !this.verificationPending()) return;
    this.verificationPending.set(false);
    this.verifiedInOtherTab.set(true);
    clearInterval(this.#cooldownIntervalId);
  }

  #handleVerificationToken(token: string) {
    this.isVerifyingToken.set(true);

    this.accountService
      .getSignupVerification(token)
      .pipe(finalize(() => this.isVerifyingToken.set(false)))
      .subscribe({
        next: (res: any) => {
          // The backend returns the record either raw ({ email, state }) or inside
          // the standard ApiResponse envelope ({ data: { email, state } }).
          const record = (res?.data ?? res) as SignupVerificationRecord;
          if (res?.error || !record?.email) {
            this.verificationError.set(res?.status === HTTP_STATUS_CODES.GONE ? 'expired' : 'invalid');
            this.#stripTokenFromUrl();
            this.cdr.detectChanges();
            return;
          }

          this.#restoreFromVerification(token, record);
        }
      });
  }

  #restoreFromVerification(token: string, record: SignupVerificationRecord) {
    const { email, state } = record;

    // The signup started in the direct flow, whose stepper has a different set of
    // steps. That choice has to be made while rendering on the server (see
    // #detectDirectSignup), so flipping it here — after hydration — would leave the
    // stepper empty. Re-enter through a URL carrying the params instead; on the next
    // load the constructor picks them up and the token restores normally.
    const directParams = state?.directSignupParams;
    if (directParams && !this.isDirectSignup() && isPlatformBrowser(this.platformId)) {
      const query = new URLSearchParams({
        from: directParams.from,
        pid: String(directParams.productId),
        vid: String(directParams.variantId),
        mid: String(directParams.membershipId),
        [VERIFICATION_TOKEN_QUERY_PARAM]: token,
      });
      window.location.assign(`/signup?${query.toString()}`);
      return;
    }

    this.verificationToken = token;
    this.#verifiedEmail = email;
    this.directSignupEmail.set(email);
    this.deliveryAddressInformation = signal(state?.deliveryAddressInformation ?? null);
    this.deliverySelectedDay = state?.deliverySelectedDay ?? null;
    this.deliverySelectedTimeWindow = state?.deliverySelectedTimeWindow ?? null;
    this.deliveryOptionSelected = state?.deliveryOptionSelected ?? null;

    if (state?.pickupInfo)
      this.pickupInfo.set(state.pickupInfo);

    if (state?.navegoAddress) {
      this.#restoredNavegoAddress = state.navegoAddress as NavegoAddress;
      this.addressService.navegoAddress.set(state.navegoAddress);
    }

    if (state?.signupPromotionInfo && !this.signupPromotionInfo())
      this.signalsStoreService.setSignupPromotionInfo(state.signupPromotionInfo);

    // Put the attribution back where account creation reads it from, so the referral
    // reaches the backend even when this browser never saw the referral link (DEV-11494).
    // Only when this browser has none of its own: an attribution captured in THIS session
    // is more recent than the parked copy, so it must not be overwritten.
    if (state?.marketingReferral && !this.localStorageService.get(LOCALSTORAGE_KEYS.MARKETING_REFERRAL))
      this.localStorageService.set(LOCALSTORAGE_KEYS.MARKETING_REFERRAL, state.marketingReferral);

    if (state?.deliveryOptionSelected)
      this.localStorageService.set(LOCALSTORAGE_KEYS.SIGNUP_SELECTED_MEMBERSHIP, state.deliveryOptionSelected);

    // Prefill the Step 1 child too, so a bounce back to it (e.g. the selected time
    // slot became full) keeps the verified email in place.
    const checkAddress = this.checkAddressComponent();
    checkAddress.email.set(email);
    checkAddress.emailTouched.set(true);

    // Advance exactly as a normal Step 1 submission would. The stepper is linear, so
    // jumping to an arbitrary index is refused; going through the regular path also
    // runs the per-flow side effects (premium memberships for the CSA step, settings).
    // Note this lands on CSA Option in the normal flow and on Personal Information in
    // the direct one — in both cases, the step right after Delivery Info.
    //
    // #getInfoForStep5() is deliberately NOT called here: it hits the authenticated
    // /account/delivery-info, and the account does not exist yet, so it 403s and the
    // global handler treats it as an expired session and redirects out. The normal
    // flow already calls it after createNewClient() succeeds.
    this.#advanceFromFirstStep();
    this.firstStep().editable = false;

    // The banner stays visible for the step the user lands on (the template also
    // hides it once the stepper moves on).
    this.showVerifiedBanner.set(true);

    this.#broadcastVerified(email);
    // The token stays in the URL on purpose: the GET is idempotent within its TTL,
    // so a page refresh re-consumes it and restores the signup at Step 2 instead of
    // starting over. It stops working once the account is created or the TTL ends.
    this.cdr.detectChanges();
  }

  #broadcastVerified(email: string) {
    if (!isPlatformBrowser(this.platformId)) return;

    try {
      if ('BroadcastChannel' in window) {
        const channel = new BroadcastChannel(SIGNUP_VERIFICATION_CHANNEL);
        channel.postMessage({ type: 'verified', email });
        channel.close();
      }
    } catch { /* sync is best-effort; verification itself already succeeded */ }

    // storage-event fallback: fires only in the other tabs.
    this.localStorageService.set(LOCALSTORAGE_KEYS.SIGNUP_EMAIL_VERIFIED, { email, verifiedAt: new Date().toISOString() });
    this.localStorageService.remove(LOCALSTORAGE_KEYS.SIGNUP_EMAIL_VERIFIED);
  }

  #stripTokenFromUrl() {
    const urlTree = this.#router.parseUrl(this.#router.url);
    delete urlTree.queryParams[VERIFICATION_TOKEN_QUERY_PARAM];
    this.#location.replaceState(urlTree.toString());
  }

  //#endregion

  createNewClient() {
    if (this.shouldDisableCreateClientButton()) return;

    if (this.#restoredNavegoAddress && !this.addressService.navegoAddress())
      this.addressService.navegoAddress.set(this.#restoredNavegoAddress);

    this.shouldDisableCreateClientButton.set(true);
    const payload = this.setUpNewClientPayload();
    if (!payload) return;
    this.accountService.create(payload).pipe(
      finalize(() => this.shouldDisableCreateClientButton.set(false))
    ).subscribe({
      next: (res: ApiResponse<Session>) => {
        const { data, status } = res;
        if (status === HTTP_STATUS_CODES.CONFLICT)
          return this.#showModalEmailAlreadyInUse(payload.accountInfo.email);
        if (data?.isTimeSlotCapacityFull) {
          this.firstStep().editable = true;
          this.firstStep().completed = false;
          this.matStepper().previous();
        } else if (data) {

          if (this.deliveryOptionSelected.configuration.requirePaymentMethod)
            this.accountService.setMustFetchStates();

          this.#trackAccountDetailsFunnelEvent();
          this.personalInformationStep().completed = true;
          this.matStepper().next();
          this.shouldDisableCreateClientButton.set(false);
          if (!this.isDirectSignup()) {
            this.#getInfoForStep5();
          }
        }
      }
    });
  }

  #trackAccountDetailsFunnelEvent() {
    const info = this.personalInformationData();
    if (!info) return;
    const requireAddress = !!this.deliveryOptionSelected?.configuration?.requireAddress;
    const nav = this.addressService.navegoAddress();
    if (requireAddress && !nav) return;
    this.#dataLayerService.trackSignUpFunnelAccountDetailsEvent({
      funnel_step: DATA_LAYER_SIGN_UP_FUNNEL_STEP_OPTIONS.account_details,
      funnel_step_number: 2,
      delivery_type: requireAddress ? 'delivery' : 'pickup',
      ...(requireAddress
        ? { delivery: { zip_code: this.deliveryAddressInformation()?.zipCode ?? '', zone: nav!.zoneName, in_area: true } }
        : { pickup: this.pickupInfo() }),
      user: {
        email: info.email ?? this.directSignupEmail(),
        phone: info.phone ?? '',
        first_name: info.firstName ?? '',
        last_name: info.lastName ?? '',
      },
    });
  }

  #showModalEmailAlreadyInUse(email: string, onTryAnother?: () => void) {
    this.#modalContentService.openModal(ModalContentTypes.CONFIRMATION, {
      title: 'Email already in use',
      textContent: `Hey! Looks like you already have an account with us. Would you like to login with <b><i>${email}</i></b>?`,
      cancelButtonText: 'Try another email',
      confirmButtonText: 'Click here to login',
    }).closed
      .subscribe((res: { confirm: boolean } | null) => {
        if (!res?.confirm) {
          if (onTryAnother) return onTryAnother();
          return this.personalInformationComponent().clearEmailField();
        }
        this.localStorageService.set(LOCALSTORAGE_KEYS.PRELOADED_EMAIL, email);
        this.#router.navigate(['/login']);
      })
  }

  onEmailAlreadyInUse(email: string) {
    this.#showModalEmailAlreadyInUse(email, () => {
      this.checkAddressComponent().clearAndFocusEmail();
    });
  }

  // The sign up is considered complete once the customer is billable, which is the
  // single point both the normal and the direct flows go through.
  onPaymentMethodCreated() {
    this.#trackCompleteFunnelEvent();
  }

  #trackCompleteFunnelEvent() {
    const session: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    if (!session?.accountInfo) return;

    // The signup can be resumed in a fresh session (log in, come back to /signup and
    // finish at the billing step), and then none of the in-memory Step 1 state is
    // around. Fall back to the session, which carries the same facts, so the event
    // never reports a pickup signup as a delivery one or vice versa.
    const membershipConfig = this.deliveryOptionSelected?.configuration;
    const requireAddress = membershipConfig
      ? !!membershipConfig.requireAddress
      : !!session.settings?.requireAddress;

    const nav = this.addressService.navegoAddress();
    const zipCode = this.deliveryAddressInformation?.()?.zipCode
      ?? (session.address?.zip != null ? String(session.address.zip) : '');
    const pickup = this.pickupInfo();
    const hasPickupInfo = !!(pickup.location || pickup.day || pickup.zone);

    this.#dataLayerService.trackSignUpFunnelCompleteEvent({
      funnel_step: DATA_LAYER_SIGN_UP_FUNNEL_STEP_OPTIONS.sign_up_complete,
      funnel_step_number: 3,
      delivery_type: requireAddress ? 'delivery' : 'pickup',
      ...(requireAddress
        ? { delivery: { zip_code: zipCode, zone: nav?.zoneName ?? '', in_area: true } }
        : {
          pickup: hasPickupInfo ? pickup : {
            location: session.pickUpInfo?.location ?? session.pickUpInfo?.name ?? '',
            day: session.deliveryInfo?.deliveryDay ?? '',
            zone: session.address?.zoneId != null ? String(session.address.zoneId) : '',
          }
        }),
      user: {
        id: session.accountInfo.id.toString(),
        logged_in: true,
        customer_type: 'new',
        email: session.accountInfo.email,
        phone: session.accountInfo.phone,
        first_name: session.accountInfo.firstName,
        last_name: session.accountInfo.lastName,
      },
    });
  }

  #getInfoForStep5() {
    this.getSignupProducts();
    this.#headerService.getFrequencies();
    this.#deliveriesService.getDeliveryZoneInfo().subscribe();
  }

  private setUpNewClientPayload(): NewAccountPayload | null {
    try {
      const {
        firstName,
        lastName,
        phone,
        email,
        password,
        additionalInfo,
        referralSource,
        birthday,
        advertisement,
        dietaryRestrictions = this.dietaryRestrictionsValues,
        contactPerson: contact,
        receiveNewsletter,
        receiveText,
        receiveTextDelivery
      } = this.personalInformationData() || {};
      const { additionalAddress: additional, zipCode: zip, deliveryNotes: noteDriver } = this.deliveryAddressInformation()
      const { street, zoneId, city, state: stateCode, latitude, longitude } = this.addressService.navegoAddress() as NavegoAddress || {}

      const res = {
        accountInfo: {
          dietaryPreferences: dietaryRestrictions?.filter((value: any) => value.completed).map((value: any) => { return { id: value.id, name: value.name } }) || null,
          email,
          firstName,
          lastName,
          noteDriver,
          password,
          phone,
          birthday
        },
        settings: {
          receiveNewsletter,
          receiveText,
          receiveTextDelivery,
          referral: {
            additionalInfo: additionalInfo || null,
            source: referralSource
          },
          delivery: {
            type: this.#premiumMembershipId || this.deliveryOptionSelected.id,
            dayId: this.deliverySelectedDay.id,
            timeWindowId: this.deliverySelectedTimeWindow === -1 ? null : this.deliverySelectedTimeWindow
          },
          peopleShoppingForId: this.shopforValue
        },
        contact
      } as NewAccountPayload

      if (this.deliveryOptionSelected.configuration.requireAddress) {
        res.address = {
          additional: additional,
          city: city,
          latitude: latitude,
          longitude: longitude,
          stateCode: stateCode,
          street: street,
          zip: zip,
          zoneId: zoneId || this.deliverySelectedDay.zoneId
        }
      } else {
        res.address = {
          zoneId: this.deliverySelectedDay.zoneId
        }
      }

      if (this.verificationToken)
        res.verificationToken = this.verificationToken;

      return res;
    } catch (error) {
      console.log('error >>>', error);
      return null;
    }
  }

  updateCreatedClient() {
    if (this.matStepper().selectedIndex === 2 && !this.deliveryOptionSelected.configuration.requirePaymentMethod
    ) {
      this.paymentMethodStepControl.disable();
      this.skipPaymentMethodStep = true;
      this.matStepper().selectedIndex = 3;
      this.getSignupProducts();
      this.matStepper().next();
      // No billing step for this membership, so the funnel would never reach its last
      // step through onPaymentMethodCreated(). Close it here instead.
      this.#trackCompleteFunnelEvent();
    } else {
      this.accountService.setMustFetchStates();
    }
    const payload = this.setUpUpdateClientPayload();
    this.profileService.putPreferences(payload).pipe(
      tap(() => {
        this.matStepper().next();
      })
    ).subscribe()
  }

  private setUpUpdateClientPayload(): any | null {
    try {
      const { dietaryRestrictions = this.dietaryRestrictionsValues } = this.personalInformationData() || {};
      return {
        accountInfo: {
          dietaryPreferences: dietaryRestrictions?.filter((value: any) => value.completed).map((value: any) => { return { id: value.id, name: value.name } }) || null,
        },
        settings: {
          peopleShoppingForId: this.shopforValue
        }
      } as any
    } catch (error) {
      console.log('error >>>', error);
      return null;
    }
  }

  continue(premiumMembershipId?: number) {
    // Email verification gate. Only Step 1 is intercepted — `continue()` is shared
    // with the CSA and billing steps, which must keep advancing normally.
    if (this.matStepper().selectedIndex === 0) {
      // Already verified in this tab (e.g. bounced back from a full time slot):
      // skip straight through instead of sending another email.
      if (this.verificationToken && this.#verifiedEmail === this.directSignupEmail()) {
        this.#advanceFromFirstStep(premiumMembershipId);
        return;
      }

      this.#sendVerificationEmail(true);
      return;
    }

    this.#advanceFromFirstStep(premiumMembershipId);
  }

  #advanceFromFirstStep(premiumMembershipId?: number) {
    this.firstStep().completed = true;

    if (this.isDirectSignup()) {
      // Direct flow: steps are Delivery(0), PersonalInfo(1), Payment(2)
      if (this.matStepper().selectedIndex === 2)
        this.paymentMethodStep().completed = true;

      this.matStepper().next();

      if (this.matStepper().selectedIndex === 1) {
        this.firstStep().editable = false;
        if (this.shouldSetUpSettings) {
          this.getAccountSettings();
          this.shouldSetUpSettings = false;
        }
      }

      // After payment completion, finish direct signup
      if (this.paymentMethodStep().completed) {
        this.#completeDirectSignup();
      }
    } else {
      // Original flow
      if (this.matStepper().selectedIndex === 3)
        this.paymentMethodStep().completed = true;

      this.matStepper().next();

      if (this.matStepper().selectedIndex === 1) {
        this.#getPremiumMembership();
        this.firstStep().editable = false;

        if (this.shouldSetUpSettings) {
          this.getAccountSettings();
          this.shouldSetUpSettings = false;
        }
      } else if (this.matStepper().selectedIndex === 2)
        this.#premiumMembershipId = premiumMembershipId;
    }
  }

  private getSignupProducts() {
    this.productsService.getSignupProducts().pipe(
      tap((response) => {
        this.subscriptionCard.set(response.data)
      })
    ).subscribe()
  }

  private getAccountSettings() {
    this.accountService.getAccountSettings().subscribe({
      next: (accountSettings: AccountSettings | null) => {
        if (accountSettings) {
          this.setUpAccountSettings(accountSettings);
        }
      }
    });

  }

  private setUpAccountSettings(accountSettings: AccountSettings) {
    this.personalInformation.set({
      contactPerson: {
        email: null,
        firstName: null,
        lastName: null,
        phone: null,
      },
      dietaryRestrictions: accountSettings.dietaryPreferences?.map((preference: ArrayOptionsItem) => {
        return { ...preference, completed: false };
      }) || [],
      peopleShoppingFor: accountSettings.peopleShoppingFor || [],
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      phone: '',
      referralSource: '',
      advertisement: '',
      shopfor: null,
      receiveNewsletter: true,
      receiveText: true
    });

    this.referralSource.set(accountSettings.references);
  }

  #getPremiumMembership() {

    this.accountService
      .getPremiumMembership(this.deliveryOptionSelected.id)
      .pipe(
        tap(response => this.premiumMemberships.set(response))
      )
      .subscribe();
  }

  sanitizeHtml(html: string): SafeHtml {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return this.#sanitizer.bypassSecurityTrustHtml(doc.body.innerHTML);
  }
  applySignupCoupon(code: string) {

    if (!code) return this.#notificationService.show({ text: 'You should provide a coupon code.', type: 'error' });

    this.isTryingToApplyCoupon.set(true);

    const marketingReferral: MarketingReferral = {
      utmCampaign: code
    };

    checkSignupCouponCodes({
      marketingReferral,
      signalsStoreService: this.signalsStoreService,
      localStorageService: this.localStorageService,
      promotionsService: this.#promotionsService,
      showDefaultMessage: true
    }).pipe(
      tap(res => {
        if (res.name) {
          const currentPath = this.#location.path().split('?')[0];
          this.#location.replaceState(currentPath);
        }
      })
    ).subscribe();

  }

  clearSignupPromotionInfo() {
    this.signalsStoreService.setSignupPromotionInfo(null);
  }

  restoreSignupInfo() {
    const signupPromotionInfo = this.localStorageService.get(LOCALSTORAGE_KEYS.PROMOTION_INFO);
    if (!signupPromotionInfo) return;
    this.signalsStoreService.setSignupPromotionInfo(signupPromotionInfo);
  }

  #fetchDirectSignupProduct(productId: number, variantId: number) {
    this.productsService.getProductById(productId).subscribe({
      next: (res) => {
        if (res?.data) {
          this.directSignupProduct.set(res.data);
          const variant = res.data.attributes
            ?.flatMap(attr => attr.values)
            .find(v => v.variantId === variantId) || null;
          this.directSignupVariant.set(variant || { variantId });
        }
      }
    });
  }

  private async getDeliveryZonInfo(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.#deliveriesService.getDeliveryZoneInfo().subscribe({
        complete: () => resolve()
      });
    })
  }

  async #completeDirectSignup() {

    await this.getDeliveryZonInfo();

    const product = this.directSignupProduct();
    const variant = this.directSignupVariant();

    if (product && variant) {
      const session: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
      const startDate = session?.deliveryInfo?.deliveryDate ?? '';
      const frequencies = this.signalsStoreService.frequencies();
      const frequencyId = frequencies[0]?.id || null;

      if (!frequencyId || !startDate) {
        this.#redirectAfterDirectSignup();
        return;
      }

      const subscription: SubscriptionPayload = {
        variantId: variant.variantId,
        quantity: 1,
        frequencyId,
        startDate,
        packageId: undefined,
      };

      this.#subscriptionsService.create(subscription).pipe(
        tap(res => {
          if (res.data?.length) {
            this.#orderService.getOrder();
          }
          this.#redirectAfterDirectSignup();
        })
      ).subscribe();
    } else {
      this.#redirectAfterDirectSignup();
    }
  }

  #redirectAfterDirectSignup() {
    this.localStorageService.set(LOCALSTORAGE_KEYS.FROM_SIGNUP, true);
    this.localStorageService.set(LOCALSTORAGE_KEYS.NEW_ACCOUNT, true);

    if (isPlatformBrowser(this.platformId)) {
      this.#location.replaceState('/shop');
      window.location.reload();
    }
  }

}
